// Shared precon decklist parse/format (mirrors Precons::Decklist).
// Identity cards are plain "1 Name" lines — kind decides the slot (no Hero: tags).

const MAIN_KINDS = new Set([
  "weapon",
  "armor",
  "shield",
  "accessory",
  "relic",
  "technique",
  "spell",
  "companion",
  "consumable",
  "tool"
])

export function foldName(str) {
  return String(str || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase("en-US")
    .trim()
    .replace(/\s+/g, " ")
}

export function nameIndex(catalog) {
  const index = new Map()
  for (const defn of catalog || []) {
    const name = defn.display_name || defn.card_id
    const key = foldName(name)
    if (!key) continue
    if (!index.has(key)) index.set(key, [])
    index.get(key).push(defn)
  }
  return index
}

function resolveName(rawName, byName) {
  const key = foldName(rawName)
  const matches = byName.get(key) || []
  if (matches.length === 0) {
    return { error: `unknown_card:${String(rawName).trim()}` }
  }
  if (matches.length > 1) {
    const ids = matches.map((d) => d.card_id).join(",")
    return { error: `ambiguous_card:${String(rawName).trim()}:${ids}` }
  }
  return { defn: matches[0] }
}

export function parseDecklist(text, catalog) {
  const byName = nameIndex(catalog)
  let heroCardId = null
  let backgroundCardId = null
  let subclassCardId = null
  let heroDisplayName = null
  const counts = {}
  const errors = []
  const warnings = []
  let matchedLines = 0

  for (const raw of String(text || "").split(/\r?\n/)) {
    const line = raw.trim()
    if (!line || line.startsWith("#")) continue

    let qty = 1
    let name = line
    const qtyMatch = line.match(/^(\d+)\s+(.+)$/)
    if (qtyMatch) {
      qty = Number(qtyMatch[1])
      name = qtyMatch[2]
    }
    if (qty < 1) {
      errors.push(`invalid_qty:${name}`)
      continue
    }

    const resolved = resolveName(name, byName)
    if (resolved.error) {
      errors.push(resolved.error)
      continue
    }

    const defn = resolved.defn
    const kind = String(defn.card_kind || "")
    const cardId = defn.card_id
    const display = defn.display_name || cardId

    if (kind === "hero") {
      if (qty !== 1) warnings.push(`identity_qty:${display}`)
      heroCardId = cardId
      heroDisplayName = display
      matchedLines += 1
      continue
    }
    if (kind === "background") {
      if (qty !== 1) warnings.push(`identity_qty:${display}`)
      backgroundCardId = cardId
      matchedLines += 1
      continue
    }
    if (kind === "subclass") {
      if (qty !== 1) warnings.push(`identity_qty:${display}`)
      subclassCardId = cardId
      matchedLines += 1
      continue
    }
    if (!MAIN_KINDS.has(kind)) {
      errors.push(`invalid_kind:${String(name).trim()}`)
      continue
    }

    let next = (counts[cardId] || 0) + qty
    if (next > 4) {
      warnings.push(`copy_cap:${display}`)
      next = 4
    }
    counts[cardId] = next
    matchedLines += 1
  }

  const mainDeckSize = Object.values(counts).reduce((a, b) => a + b, 0)
  return {
    hero_card_id: heroCardId,
    background_card_id: backgroundCardId,
    subclass_card_id: subclassCardId,
    hero_display_name: heroDisplayName,
    counts,
    errors: [...new Set(errors)],
    warnings: [...new Set(warnings)],
    main_deck_size: mainDeckSize,
    matched_lines: matchedLines
  }
}

export function formatDecklist({ heroCardId, backgroundCardId, subclassCardId, counts, catalog }) {
  const byId = Object.fromEntries((catalog || []).map((c) => [c.card_id, c]))
  const lines = []

  ;[heroCardId, backgroundCardId, subclassCardId].forEach((cardId) => {
    if (!cardId) return
    const defn = byId[cardId]
    lines.push(`1 ${defn?.display_name || cardId}`)
  })

  Object.entries(counts || {})
    .sort(([a], [b]) => {
      const an = foldName(byId[a]?.display_name || a)
      const bn = foldName(byId[b]?.display_name || b)
      return an.localeCompare(bn)
    })
    .forEach(([cardId, count]) => {
      const defn = byId[cardId]
      lines.push(`${count} ${defn?.display_name || cardId}`)
    })

  return lines.length ? `${lines.join("\n")}\n` : ""
}

export function previewSummary(parsed, identity = {}) {
  const byId = Object.fromEntries((identity.catalog || []).map((c) => [c.card_id, c]))
  const label = (cardId, fallbackName) => {
    if (!cardId) return "—"
    const defn = byId[cardId]
    return defn?.display_name || fallbackName || cardId
  }

  const heroId = identity.hero_card_id || parsed.hero_card_id
  const bgId = identity.background_card_id || parsed.background_card_id
  const scId = identity.subclass_card_id || parsed.subclass_card_id

  const lines = [
    `Hero: ${label(heroId, parsed.hero_display_name)}`,
    `Background: ${label(bgId)}`,
    `Subclass: ${label(scId)}`
  ]

  const parts = []
  if (parsed.matched_lines) parts.push(`${parsed.matched_lines} matched`)
  parts.push(`${parsed.main_deck_size}/60 main`)
  if (parsed.warnings?.length) parts.push(`${parsed.warnings.length} warning(s)`)
  if (parsed.errors?.length) parts.push(`${parsed.errors.length} error(s)`)
  lines.push(parts.join(" · "))
  return lines.join("\n")
}
